import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, FileText, LoaderCircle } from 'lucide-react';
import DOMPurify from 'dompurify';
import { apiFetch } from '../../lib/api';
import { track } from '../../lib/analytics';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import type { Article } from './ArticleListPanel';

export interface ArticleContentPanelProps {
  feedId: string;
  article: Article | null;
}

interface ExtractResponse {
  success: boolean;
  content_html?: string;
  title?: string;
  byline?: string | null;
  excerpt?: string | null;
  word_count?: number;
  read_time_minutes?: number;
  error?: string;
}

type PreviewStatus = 'idle' | 'loading' | 'loaded' | 'failed';

interface PreviewData {
  html: string;
  readTimeMinutes: number;
  byline: string | null;
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function textFromHtml(value: string): string {
  if (!value) return '';
  if (typeof window === 'undefined') {
    return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const element = window.document.createElement('div');
  element.innerHTML = value;
  return (element.textContent ?? element.innerText ?? '').replace(/\s+/g, ' ').trim();
}

export function ArticleContentPanel({ feedId, article }: ArticleContentPanelProps) {
  const { t } = useTranslation('feed-builder');
  const extractionEnabled = useFeatureFlag('article-extraction-enabled');

  const [previewStatus, setPreviewStatus] = useState<PreviewStatus>('idle');
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const activeArticleIdRef = useRef<string | null>(null);

  useEffect(() => {
    activeArticleIdRef.current = article?.id ?? null;
    setPreviewStatus('idle');
    setPreviewData(null);
  }, [article]);

  const loadPreview = async () => {
    if (!article || previewStatus === 'loading') return;

    setPreviewStatus('loading');
    setPreviewData(null);

    try {
      const res = await apiFetch('/articles/extract', {
        method: 'POST',
        body: JSON.stringify({ url: article.url }),
      });
      if (activeArticleIdRef.current !== article.id) return;

      if (!res.ok) {
        setPreviewStatus('failed');
        track('optional_article_preview_failed', {
          feed_id: feedId,
          article_id: article.id,
          reason: 'request_failed',
        });
        return;
      }

      const body = (await res.json()) as ExtractResponse;
      if (activeArticleIdRef.current !== article.id) return;

      if (!body.success || !body.content_html) {
        setPreviewStatus('failed');
        track('optional_article_preview_failed', {
          feed_id: feedId,
          article_id: article.id,
          reason: body.error ?? 'no_content',
        });
        return;
      }

      setPreviewData({
        html: DOMPurify.sanitize(body.content_html, { USE_PROFILES: { html: true } }),
        readTimeMinutes: body.read_time_minutes ?? 0,
        byline: body.byline ?? article.author ?? null,
      });
      setPreviewStatus('loaded');
    } catch (err) {
      if (activeArticleIdRef.current !== article.id) return;
      setPreviewStatus('failed');
      track('optional_article_preview_failed', {
        feed_id: feedId,
        article_id: article.id,
        reason: (err as { name?: string })?.name ?? 'unknown',
      });
    }
  };

  if (!article) {
    return (
      <section className="flex h-full flex-1 items-center justify-center bg-white">
        <div className="max-w-sm px-6 text-center">
          <FileText size={28} strokeWidth={1.5} className="mx-auto mb-3 text-brand-muted" />
          <p className="m-0 text-sm font-medium text-brand-text">
            {t('reader.content.empty')}
          </p>
          <p className="m-0 mt-1 text-[13px] leading-5 text-brand-muted">
            {t('reader.content.emptyHint')}
          </p>
        </div>
      </section>
    );
  }

  const sourceDomain = hostnameOf(article.source_url || article.url);
  const excerpt = textFromHtml(article.excerpt);

  const handleOpenOriginal = () => {
    track('article_open_original', {
      feed_id: feedId,
      article_id: article.id,
      source: 'output_workspace',
    });
  };

  const byline = [
    previewData?.byline ?? article.author,
    sourceDomain,
    article.published_at ? formatDate(article.published_at) : null,
    previewData && previewData.readTimeMinutes > 0
      ? t('reader.content.minRead', { count: previewData.readTimeMinutes })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <section className="flex h-full min-w-0 flex-1 flex-col bg-white">
      <header className="flex shrink-0 items-center gap-3 border-b border-brand-border bg-white/85 px-7 py-3 backdrop-blur-[16px]">
        <div className="min-w-0 flex-1">
          <h2
            className="m-0 truncate text-[13.5px] font-semibold leading-5 tracking-[-0.01em] text-brand-text"
            title={article.title}
          >
            {article.title}
          </h2>
          <p className="mt-0.5 truncate text-[11.5px] leading-4 text-brand-muted">
            {sourceDomain}
            {article.published_at ? ` · ${formatDate(article.published_at)}` : ''}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-accent-green-light px-3 py-[3px] text-[11px] font-semibold leading-4 text-accent-green-dark">
          {previewStatus === 'loaded'
            ? t('reader.content.previewBadge')
            : t('reader.content.outputBadge')}
        </span>
        <a
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleOpenOriginal}
          aria-label={t('reader.content.openOriginal')}
          title={t('reader.content.openOriginal')}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-0 bg-black/5 text-brand-text no-underline transition-colors hover:bg-black/[0.09]"
        >
          <ExternalLink size={13} strokeWidth={2} />
        </a>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <article className="mx-auto max-w-[640px] px-8 pb-20 pt-14 font-serif">
          <h1 className="m-0 text-[34px] font-medium leading-[1.2] tracking-[-0.01em] text-brand-text">
            {article.title}
          </h1>
          {byline && (
            <p className="mt-3.5 font-sans text-sm leading-[1.5] text-brand-muted">{byline}</p>
          )}

          <div className="mt-8 text-[19px] leading-[1.65] text-brand-text">
            {previewStatus === 'loaded' && previewData ? (
              <div
                className="reader-prose reader-prose-body"
                dangerouslySetInnerHTML={{ __html: previewData.html }}
              />
            ) : (
              <p className="m-0 whitespace-pre-line">
                {excerpt || t('reader.content.noExcerpt')}
              </p>
            )}
          </div>

          {extractionEnabled && previewStatus !== 'loaded' && (
            <div className="mt-8 border-t border-brand-border pt-6 font-sans">
              <button
                type="button"
                onClick={loadPreview}
                disabled={previewStatus === 'loading'}
                className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-brand-border-medium bg-white px-4 text-sm font-medium leading-5 text-brand-text transition-colors hover:bg-brand-surface disabled:cursor-wait disabled:opacity-60"
              >
                {previewStatus === 'loading' ? (
                  <LoaderCircle size={14} strokeWidth={2} className="animate-spin" />
                ) : (
                  <FileText size={14} strokeWidth={2} />
                )}
                {previewStatus === 'loading'
                  ? t('reader.content.previewLoading')
                  : t('reader.content.loadPreview')}
              </button>
              <p className="m-0 mt-2 text-[13px] leading-5 text-brand-muted">
                {previewStatus === 'failed'
                  ? t('reader.content.previewFailed')
                  : t('reader.content.previewDescription')}
              </p>
            </div>
          )}
        </article>
      </div>
    </section>
  );
}
